import React, { useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Loading } from "../../sharedComponents/Loading";
import { DataNotFound } from "../../sharedComponents/DataNotFound";
import { useHiperlyncLinks } from "../../../hooks/useClipping/useHiperlyncLinks";

export const ClippingDownload = ({
  open,
  onClose,
  downloadQueue,
  setDownloadQueue,
  hiperlync = false,
  token,
  selectedDate,
  setHiperlyncQueue,
  hiperlyncQueue,
}) => {
  const { links: hiperLinks, loading: loadingLinks } = useHiperlyncLinks(token, selectedDate, open, hiperlync);
  const [copiedIndex, setCopiedIndex] = React.useState(null);

  useEffect(() => {
    const removeDuplicatedHiperlync = () => {
      setHiperlyncQueue((prevQueue) => {
        const map = new Map();
        prevQueue.forEach((item) => {
          map.set(item.nameDocument, item);
        });
        return Array.from(map.values());
      });
    };

    removeDuplicatedHiperlync();
  }, [setHiperlyncQueue]);

  const handleDownload = async (url, name, index) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name.endsWith(".pdf") ? name : `${name}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);

      handleDelete(index);
    } catch (error) {
      console.error("Error en la descarga programática", error);
      window.open(url, "_blank");
      handleDelete(index);
    }
  };

  const handleClickHiperlync = (index, url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    handleDeleteHiperlync(index);
  };

  const handleDeleteHiperlync = (index) => {
    setHiperlyncQueue((prevQueue) => prevQueue.filter((_, i) => i !== index));
  };

  const handleDelete = (index) => {
    setDownloadQueue((prevQueue) => prevQueue.filter((_, i) => i !== index));
  };

  const formatDate = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleDateString("es-ES");
  };

  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString("es-ES", { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          p: 0,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, borderBottom: "1px solid #ddd" }}>
          <Typography variant="h6" fontWeight="bold">Datos Generados</Typography>
          <IconButton onClick={onClose} sx={{ color: "gray" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 2, overflowY: "auto", flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Cola de Descargas
          </Typography>
          {downloadQueue.length === 0 ? (
            <DataNotFound />
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell># PDF</TableCell>
                    <TableCell>Nombre del Archivo</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Estado / Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloadQueue.map((file, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{file.nameDocument}</TableCell>
                      <TableCell>{file.fecha}</TableCell>
                      <TableCell>{file.generatedAt}</TableCell>
                      <TableCell>
                        {file.status === "processing" ? (
                          <Box sx={{ width: '100%', minWidth: 120 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {file.message} ({file.progress}%)
                            </Typography>
                            <LinearProgress variant="determinate" value={file.progress} sx={{ height: 8, borderRadius: 5 }} />
                          </Box>
                        ) : file.status === "error" ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                              Error: {file.message}
                            </Typography>
                            <IconButton onClick={() => handleDelete(index)} size="small" color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex' }}>
                            <IconButton color="primary" onClick={() => handleDownload(file.url, file.nameDocument, index)}>
                              <DownloadIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDelete(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {hiperlync ? (
            <>
            {hiperlyncQueue.length > 0 && (
              <>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                Hipervínculos generados
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell># PDF</TableCell>
                      <TableCell>Nombre del Archivo</TableCell>
                      <TableCell>Enlace</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hiperlyncQueue.map((file, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{file.nameDocument}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: "italic",
                              cursor: "pointer",
                              color: "blue",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                            onClick={() => handleClickHiperlync(index, file.hiperlync)}
                          >
                            Ver enlace
                          </Typography>
                          <Tooltip title={copiedIndex === index ? "Enlace copiado" : "Copiar"} arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const text = file.hiperlync;

                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                  navigator.clipboard.writeText(text)
                                    .then(() => {
                                      setCopiedIndex(index);
                                      setTimeout(() => setCopiedIndex(null), 1500);
                                    })
                                    .catch((err) => {
                                      console.error("Error al copiar:", err);
                                    });
                                } else {
                                  const textArea = document.createElement("textarea");
                                  textArea.value = text;
                                  textArea.style.position = "fixed";
                                  textArea.style.top = "-1000px";
                                  document.body.appendChild(textArea);
                                  textArea.focus();
                                  textArea.select();

                                  try {
                                    document.execCommand("copy");
                                    setCopiedIndex(index);
                                    setTimeout(() => setCopiedIndex(null), 1500);
                                  } catch (err) {
                                    console.error("Error al copiar con fallback:", err);
                                  }

                                  document.body.removeChild(textArea);
                                }
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}

            {loadingLinks ? (
              <Loading message="Buscando Hipervínculos..." />
            ) : hiperLinks.length === 0 ? (
              <></>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                  Hipervínculos
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Enlace</TableCell>
                        <TableCell>Fecha Generada</TableCell>
                        <TableCell>Fecha Actualizada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hiperLinks.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.tipo}</TableCell>
                          <TableCell>
                            <Link
                              href={item.url}
                              target="_blank"
                              rel="noopener"
                              underline="hover"
                            >
                              {item.hyperlink}
                            </Link>
                            <Tooltip title={copiedIndex === index ? "Enlace copiado" : "Copiar"} arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const text = item.url;

                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(text)
                                      .then(() => {
                                        setCopiedIndex(index);
                                        setTimeout(() => setCopiedIndex(null), 1500);
                                      })
                                      .catch((err) => {
                                        console.error("Error al copiar:", err);
                                      });
                                  } else {
                                    const textArea = document.createElement("textarea");
                                    textArea.value = text;
                                    textArea.style.position = "fixed";
                                    textArea.style.top = "-1000px";
                                    document.body.appendChild(textArea);
                                    textArea.focus();
                                    textArea.select();

                                    try {
                                      document.execCommand("copy");
                                      setCopiedIndex(index);
                                      setTimeout(() => setCopiedIndex(null), 1500);
                                    } catch (err) {
                                      console.error("Error al copiar con fallback:", err);
                                    }

                                    document.body.removeChild(textArea);
                                  }
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{formatDate(item.created_at)} {formatTime(item.created_at)}</TableCell>
                          <TableCell>{formatDate(item.updated_at)} {formatTime(item.updated_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            </>
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </Modal>
  );
};